import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Server,
  Terminal,
  Network,
  Users,
  FolderTree,
  Database,
  LineChart,
  Archive,
  ShieldCheck,
  FileText,
  LogOut,
  ChevronDown,
  Plus,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext.js';
import { api } from '../lib/api.js';
import { StatusBadge } from './StatusBadge.js';
import { ServerSummary, Role } from '@kcdash/shared';

export const Layout: React.FC = () => {
  const { user, logout, hasRole } = useAuth();
  const { id: currentServerId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [servers, setServers] = useState<ServerSummary[]>([]);
  const [currentServer, setCurrentServer] = useState<ServerSummary | null>(null);
  const [isServerDropdownOpen, setIsServerDropdownOpen] = useState(false);

  useEffect(() => {
    async function loadServers() {
      try {
        const list = await api.getServers();
        setServers(list);

        if (currentServerId) {
          const match = list.find((s) => s.id === currentServerId);
          if (match) setCurrentServer(match);
        } else if (list.length > 0 && !location.pathname.startsWith('/users') && !location.pathname.startsWith('/audit')) {
          // If no server selected and on base path, set first server
          setCurrentServer(list[0]);
        }
      } catch (err) {
        console.error('Failed to load server list:', err);
      }
    }

    loadServers();
  }, [currentServerId, location.pathname]);

  const activeServerId = currentServerId || currentServer?.id;

  const navItemClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition ${
      isActive
        ? 'bg-brand-950/60 text-brand-400 border border-brand-800/60 font-semibold'
        : 'text-neutral-400 hover:text-neutral-200 hover:bg-panel-cardHover'
    }`;

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-panel-bg text-neutral-200">
      {/* Sidebar */}
      <aside className="w-64 border-r border-panel-border bg-panel-card flex flex-col justify-between select-none">
        <div>
          {/* Logo & Header */}
          <div className="h-16 flex items-center px-6 border-b border-panel-border gap-3">
            <div className="w-8 h-8 rounded-lg bg-brand-950 border border-brand-800 flex items-center justify-center">
              <span className="font-bold text-brand-400 font-mono">KC</span>
            </div>
            <div>
              <div className="font-bold text-sm text-neutral-100 tracking-wider">KCDASH</div>
              <div className="text-[10px] text-neutral-500 font-mono">KCD II SERVER PANEL</div>
            </div>
          </div>

          {/* Server Switcher Dropdown */}
          <div className="p-4 border-b border-panel-border">
            <div className="relative">
              <button
                onClick={() => setIsServerDropdownOpen(!isServerDropdownOpen)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-panel-subtle border border-panel-border text-left hover:border-brand-800/80 transition"
              >
                <div className="overflow-hidden">
                  <div className="text-xs font-semibold text-neutral-200 truncate">
                    {currentServer?.name || 'Select Server'}
                  </div>
                  <div className="text-[10px] text-neutral-500 font-mono">
                    {currentServer?.id || 'No server selected'}
                  </div>
                </div>
                <ChevronDown className="w-4 h-4 text-neutral-400" />
              </button>

              {isServerDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-panel-card border border-panel-border rounded-lg shadow-xl z-50 py-1 overflow-hidden">
                  {servers.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => {
                        setCurrentServer(s);
                        setIsServerDropdownOpen(false);
                        navigate(`/servers/${s.id}`);
                      }}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-neutral-800 transition flex items-center justify-between"
                    >
                      <span className="truncate">{s.name}</span>
                      <StatusBadge state={s.state} size="sm" />
                    </button>
                  ))}
                  <div className="border-t border-panel-border mt-1 pt-1">
                    <button
                      onClick={() => {
                        setIsServerDropdownOpen(false);
                        navigate('/servers');
                      }}
                      className="w-full text-left px-3 py-1.5 text-xs text-brand-400 hover:bg-neutral-800 flex items-center gap-1.5"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>Manage All Servers</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Server Sub-navigation */}
          {activeServerId && (
            <div className="px-3 py-3">
              <div className="px-3 pb-2 text-[10px] font-bold text-neutral-500 uppercase tracking-wider font-mono">
                Server Controls
              </div>
              <nav className="space-y-1">
                <NavLink to={`/servers/${activeServerId}`} end className={navItemClass}>
                  <Server className="w-4 h-4" />
                  <span>Overview</span>
                </NavLink>
                <NavLink to={`/servers/${activeServerId}/console`} className={navItemClass}>
                  <Terminal className="w-4 h-4" />
                  <span>Live Console</span>
                </NavLink>
                <NavLink to={`/servers/${activeServerId}/network`} className={navItemClass}>
                  <Network className="w-4 h-4" />
                  <span>Network</span>
                </NavLink>
                <NavLink to={`/servers/${activeServerId}/players`} className={navItemClass}>
                  <Users className="w-4 h-4" />
                  <span>Players</span>
                </NavLink>
                <NavLink to={`/servers/${activeServerId}/files`} className={navItemClass}>
                  <FolderTree className="w-4 h-4" />
                  <span>File Manager</span>
                </NavLink>
                <NavLink to={`/servers/${activeServerId}/database`} className={navItemClass}>
                  <Database className="w-4 h-4" />
                  <span>Database</span>
                </NavLink>
                <NavLink to={`/servers/${activeServerId}/metrics`} className={navItemClass}>
                  <LineChart className="w-4 h-4" />
                  <span>Metrics</span>
                </NavLink>
                <NavLink to={`/servers/${activeServerId}/backups`} className={navItemClass}>
                  <Archive className="w-4 h-4" />
                  <span>Backups</span>
                </NavLink>
              </nav>
            </div>
          )}

          {/* System Navigation */}
          <div className="px-3 py-3 border-t border-panel-border">
            <div className="px-3 pb-2 text-[10px] font-bold text-neutral-500 uppercase tracking-wider font-mono">
              System
            </div>
            <nav className="space-y-1">
              <NavLink to="/servers" end className={navItemClass}>
                <Server className="w-4 h-4" />
                <span>All Servers</span>
              </NavLink>

              {hasRole(Role.ADMIN) && (
                <NavLink to="/audit" className={navItemClass}>
                  <FileText className="w-4 h-4" />
                  <span>Audit Logs</span>
                </NavLink>
              )}

              {hasRole(Role.OWNER) && (
                <NavLink to="/users" className={navItemClass}>
                  <ShieldCheck className="w-4 h-4" />
                  <span>Users & RBAC</span>
                </NavLink>
              )}
            </nav>
          </div>
        </div>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-panel-border flex items-center justify-between bg-panel-subtle/50">
          <div className="overflow-hidden">
            <div className="text-xs font-semibold text-neutral-200 truncate">{user?.username}</div>
            <div className="text-[10px] text-brand-400 font-mono uppercase">{user?.role}</div>
          </div>
          <button
            onClick={() => logout()}
            title="Sign out"
            className="p-1.5 rounded-lg text-neutral-400 hover:text-red-400 hover:bg-neutral-800 transition"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Navbar */}
        <header className="h-16 border-b border-panel-border bg-panel-card px-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-base font-semibold text-neutral-100">
              {currentServer ? currentServer.name : 'Server Dashboard'}
            </h1>
            {currentServer && <StatusBadge state={currentServer.state} size="sm" />}
          </div>

          {currentServer && (
            <div className="text-xs font-mono text-neutral-400">
              {currentServer.domain || currentServer.host}:{currentServer.port}
            </div>
          )}
        </header>

        {/* Page View Container */}
        <main className="flex-1 overflow-y-auto p-8 bg-panel-bg">
          <Outlet />
        </main>
      </div>
    </div>
  );
};


